import React, { useState, useEffect, useMemo } from 'react';

const Main = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const textOptions = useMemo(() => [
    "Automate Customer Support",
    "Automate Content Creation",
    "Automate Sales & Marketing",
    "Transform Your Business"
  ], []);

  useEffect(() => {
    // Reduced loading time from 2000ms to 800ms for faster experience
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => setIsContentVisible(true), 50); // Also reduced transition delay
    }, 800);
    return () => clearTimeout(loadingTimer);
  }, []);

  useEffect(() => {
    if (!isContentVisible) return;
    const currentText = textOptions[currentTextIndex];
    let timeout;
    if (isPaused) {
      timeout = setTimeout(() => setIsPaused(false), 2000);
    } else if (!isDeleting && currentCharIndex < currentText.length) {
      timeout = setTimeout(() => setCurrentCharIndex(prev => prev + 1), 60);
    } else if (!isDeleting && currentCharIndex === currentText.length) {
      setIsPaused(true);
      setTimeout(() => setIsDeleting(true), 2000);
    } else if (isDeleting && currentCharIndex > 0) {
      timeout = setTimeout(() => setCurrentCharIndex(prev => prev - 1), 30);
    } else if (isDeleting && currentCharIndex === 0) {
      setIsDeleting(false);
      setCurrentTextIndex(prev => (prev + 1) % textOptions.length);
    }
    return () => clearTimeout(timeout);
  }, [isContentVisible, currentTextIndex, currentCharIndex, isDeleting, isPaused, textOptions]);

  const displayText = textOptions[currentTextIndex]?.substring(0, currentCharIndex) || '';

  return (
    <div className="min-h-screen overflow-hidden relative bg-[#0c1c5c] text-white">
      <style>{`
        @keyframes blink{0%,50%{opacity:1}51%,100%{opacity:0}}
        @keyframes gradient{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
        @keyframes slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes fastSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes aurora {
          0% { transform: scale(1) rotate(0deg); opacity: 0.4; }
          50% { transform: scale(1.8) rotate(180deg); opacity: 0.2; }
          100% { transform: scale(1) rotate(360deg); opacity: 0.4; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        @keyframes iconGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(42, 171, 220, 0.3), 0 0 40px rgba(42, 171, 220, 0.1); }
          50% { box-shadow: 0 0 30px rgba(176, 255, 251, 0.5), 0 0 60px rgba(176, 255, 251, 0.2); }
        }
        @keyframes cardHover {
          0% { transform: translateY(0px) scale(1); }
          100% { transform: translateY(-10px) scale(1.02); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0px); opacity: 0.7; }
          50% { transform: translateY(-10px); opacity: 1; }
        }
        @keyframes loadingFade {
          0% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-blink{animation:blink 1s infinite}
        .animate-gradient{background-size:200% 200%;animation:gradient 4s ease infinite}
        .animate-slide-up{animation:slideUp .8s ease-out forwards}
        .animate-spin{animation:spin 1s linear infinite}
        .animate-fast-spin{animation:fastSpin 0.6s linear infinite}
        .animate-aurora { animation: aurora 25s linear infinite alternate; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse { animation: pulse 3s ease-in-out infinite; }
        .animate-icon-glow { animation: iconGlow 3s ease-in-out infinite; }
        .animate-card-hover { animation: cardHover 0.3s ease-out forwards; }
        .animate-bounce { animation: bounce 1s ease-in-out infinite; }
        .animate-loading-fade { animation: loadingFade 0.8s ease-in-out forwards; }
        .delay-300{animation-delay:.3s}.delay-600{animation-delay:.6s}.delay-900{animation-delay:.9s}.delay-1200{animation-delay:1.2s}
      `}</style>

      {/* Enhanced Dynamic Aurora Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] animate-aurora" style={{ animationDelay: '0s' }}>
            <div className="absolute w-3/5 h-3/5 rounded-full bg-gradient-to-r from-[#1a4fb8] to-[#2aabdc] mix-blend-screen filter blur-3xl opacity-60" style={{top: '20%', left: '20%'}}></div>
        </div>
        <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] animate-aurora" style={{ animationDelay: '8s' }}>
            <div className="absolute w-2/5 h-2/5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#b0fffb] mix-blend-screen filter blur-3xl opacity-50" style={{top: '60%', left: '60%'}}></div>
        </div>
         <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] animate-aurora" style={{ animationDelay: '16s' }}>
            <div className="absolute w-1/3 h-1/3 rounded-full bg-gradient-to-r from-[#2aabdc] to-[#06b6d4] mix-blend-screen filter blur-3xl opacity-40" style={{top: '80%', left: '10%'}}></div>
        </div>
        {/* Additional floating particles */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i} 
              className="absolute w-2 h-2 bg-[#b0fffb] rounded-full animate-float opacity-30"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 6}s`,
                animationDuration: `${4 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="fixed inset-0 z-50 flex flex-col justify-center items-center bg-[#0c1c5c] animate-loading-fade">
          {/* Enhanced Loading Animation */}
          <div className="relative">
            {/* Outer rotating ring */}
            <div className="w-20 h-20 rounded-full border-2 border-[#2aabdc]/20 animate-fast-spin"></div>
            {/* Middle gradient ring */}
            <div className="absolute top-1 left-1 w-18 h-18 rounded-full border-2 border-transparent bg-gradient-to-r from-[#2aabdc] via-[#b0fffb] to-[#7c3aed] animate-fast-spin" style={{animationDirection: 'reverse', animationDuration: '0.8s'}}></div>
            <div className="absolute top-2 left-2 w-16 h-16 rounded-full bg-[#0c1c5c]"></div>
            {/* Inner pulsing dot */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-3 h-3 bg-gradient-to-r from-[#b0fffb] to-[#2aabdc] rounded-full animate-pulse shadow-lg shadow-[#b0fffb]/60"></div>
            </div>
            {/* Floating particles around loader */}
            {[...Array(6)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-1 h-1 bg-[#b0fffb] rounded-full animate-float opacity-60"
                style={{
                  top: `${20 + Math.sin((i * Math.PI) / 3) * 30}px`,
                  left: `${20 + Math.cos((i * Math.PI) / 3) * 30}px`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '2s'
                }}
              />
            ))}
          </div>
          
          {/* Enhanced Loading Text */}
          <div className="mt-8 text-center">
            <div className="text-[#b0fffb] text-lg font-bold mb-2 animate-pulse">Loading </div>
            <div className="flex justify-center items-center gap-1">
              {[...Array(3)].map((_, i) => (
                <div 
                  key={i}
                  className="w-2 h-2 bg-gradient-to-r from-[#2aabdc] to-[#b0fffb] rounded-full animate-bounce"
                  style={{animationDelay: `${i * 0.2}s`}}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <main className={`min-h-screen flex items-center justify-center px-6 py-20 relative z-10 transition-all duration-500 ${isContentVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-7xl w-full relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className={`${isContentVisible ? 'animate-slide-up' : 'opacity-0'}`}>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight mb-8">
                  <span className="block mb-4 text-white drop-shadow-lg">Your Path to</span>
                  <span className="block bg-gradient-to-r from-[#2aabdc] via-[#b0fffb] to-[#7c3aed] bg-clip-text text-transparent animate-gradient drop-shadow-2xl">Unstoppable Success!</span>
                  <span className="block mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white drop-shadow-lg">Multi-Agent AI that can</span>
                </h1>
              </div>
              <div className={`${isContentVisible ? 'animate-slide-up delay-300' : 'opacity-0'}`}>
                <div className="h-16 sm:h-20 lg:h-24 flex items-center justify-center lg:justify-start mb-8">
                  <span className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold inline-block text-[#b0fffb] drop-shadow-lg">
                    {displayText}
                    {!isPaused && (<span className="inline-block w-1 h-8 sm:h-10 lg:h-12 xl:h-14 ml-1 bg-[#b0fffb] animate-blink shadow-lg"></span>)}
                  </span>
                </div>
              </div>
              <div className={`${isContentVisible ? 'animate-slide-up delay-600' : 'opacity-0'}`}>
                <p className="text-lg lg:text-xl xl:text-2xl max-w-3xl mx-auto lg:mx-0 leading-relaxed text-gray-200">
                  Streamline Employee Assistance, boost productivity, and uplift Customer Satisfaction with
                  <span className="font-semibold text-[#2aabdc] drop-shadow-sm"> tailored AI solutions </span>
                  ensuring Superior Service.
                </p>
              </div>
            </div>

            <div className={`lg:col-span-1 ${isContentVisible ? 'animate-slide-up delay-900' : 'opacity-0'}`}>
              <div className="relative p-8 bg-black/30 backdrop-blur-2xl border border-[#2aabdc]/30 rounded-3xl shadow-2xl transition-all duration-500 hover:border-[#b0fffb]/60 hover:shadow-[0_0_50px_rgba(176,255,251,0.3)] hover:bg-black/40 group">
                {[
                  { title: 'Customer Support', delay: '0s', icon: 'M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 7a4 4 0 108 0 4 4 0 00-8 0z' },
                  { title: 'Investor community', delay: '1s', icon: 'M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' },
                  { title: 'Sales & Marketing', delay: '2s', icon: 'M3 3v18h18M18.7 8A6 6 0 108 2.3M13 13a4 4 0 00-8 0' },
                ].map((item, index) => (
                  <React.Fragment key={item.title}>
                    <div className="text-center group-hover:animate-float">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#2aabdc]/20 to-[#b0fffb]/20 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-500 hover:from-[#2aabdc]/40 hover:to-[#b0fffb]/40 animate-icon-glow backdrop-blur-sm border border-[#2aabdc]/20 hover:scale-110 cursor-pointer">
                          <svg className="w-10 h-10 text-[#b0fffb] drop-shadow-lg transition-all duration-300 group-hover:text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                            <path d={item.icon} />
                          </svg>
                      </div>
                      <p className="text-base font-bold text-white drop-shadow-sm tracking-wide group-hover:text-[#b0fffb] transition-colors duration-300">{item.title}</p>
                    </div>
                    {index < 2 && (
                       <div className="h-16 w-px bg-gradient-to-b from-[#2aabdc]/60 via-[#b0fffb]/80 to-[#2aabdc]/20 mx-auto my-6 animate-pulse"></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="py-24 px-6 bg-black/40 backdrop-blur-2xl relative z-10 border-t border-[#2aabdc]/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <span className="text-[#2aabdc] font-bold text-xl tracking-wider uppercase animate-pulse">What We Do</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-6 mb-8 drop-shadow-2xl">
              Transform Your Enterprise with{' '}
              <span className="bg-gradient-to-r from-[#2aabdc] via-[#b0fffb] to-[#7c3aed] bg-clip-text text-transparent animate-gradient">Agentic AI</span>
            </h2>
            <p className="text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Our Multi-Agent Platform helps enterprises automate workflows, make smarter decisions, and deliver human-like experiences—fast, secure, and built to scale.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-[#0c1c5c] relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-10">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                <span className="text-white drop-shadow-lg">Transformation with</span><br />
                <span className="text-[#b0fffb] drop-shadow-2xl animate-pulse">IVR AI</span>
              </h2>
              <div className="space-y-8">
                <p className="text-xl text-gray-200 leading-relaxed">
                  Join leading businesses using the <span className="font-bold text-white">Agentic AI Platform</span> to drive efficiency, innovation, and growth.
                </p>
              </div>
              <div className="pt-6">
                <button className ="bg-gradient-to-r from-yellow-400 to-yellow-300 hover:from-yellow-300 hover:to-yellow-400 text-[#0c1c5c] font-bold text-lg px-10 py-5 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl shadow-yellow-400/40 flex items-center gap-3 animate-pulse hover:animate-none">
                  Sign Up
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                </button>
              </div>
            </div>
            
            {/* Enhanced Image Grid with Dynamic Glassmorphism */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                  {[
                      { 
                        title: "Business Leader", 
                        aspect: "aspect-square", 
                        icon: "M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M8.5 7a4 4 0 108 0 4 4 0 00-8 0z",
                        gradient: "from-[#2aabdc]/20 to-[#b0fffb]/30"
                      },
                      { 
                        title: "Remote Work", 
                        aspect: "aspect-[4/3]", 
                        icon: "M10 21h7a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v8M15 17l-3-3l3-3",
                        gradient: "from-[#7c3aed]/20 to-[#2aabdc]/30"
                      },
                      { 
                        title: "Executive", 
                        aspect: "aspect-[3/4]", 
                        icon: "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 7a4 4 0 108 0 4 4 0 00-8 0z",
                        gradient: "from-[#b0fffb]/20 to-[#7c3aed]/30"
                      },
                      { 
                        title: "Analyst", 
                        aspect: "aspect-square", 
                        icon: "M3 3v18h18M18.7 8A6 6 0 108 2.3M13 13a4 4 0 00-8 0",
                        gradient: "from-[#2aabdc]/30 to-[#b0fffb]/20"
                      },
                  ].map((card, i) => (
                      <div key={i} className={`bg-gradient-to-br ${card.gradient} backdrop-blur-2xl border border-[#2aabdc]/30 rounded-3xl p-6 flex flex-col justify-between relative overflow-hidden transition-all duration-500 hover:border-[#b0fffb]/80 hover:bg-black/30 hover:scale-105 hover:shadow-2xl hover:shadow-[#b0fffb]/20 cursor-pointer group ${card.aspect}`}>
                          {/* Animated background glow */}
                          <div className="absolute inset-0 bg-gradient-to-br from-[#2aabdc]/10 via-transparent to-[#b0fffb]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
                          
                          {/* Enhanced Icon */}
                          <div className="relative z-10">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#2aabdc]/40 to-[#b0fffb]/40 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 animate-icon-glow backdrop-blur-sm border border-[#2aabdc]/40">
                              <svg className="w-8 h-8 text-[#b0fffb] group-hover:text-white transition-colors duration-300 drop-shadow-lg" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                  <path d={card.icon} />
                              </svg>
                            </div>
                          </div>
                          
                          {/* Enhanced Title */}
                          <h3 className="text-white font-bold text-lg group-hover:text-[#b0fffb] transition-colors duration-300 relative z-10 drop-shadow-sm">{card.title}</h3>
                          
                          {/* Floating particles effect */}
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            {[...Array(3)].map((_, j) => (
                              <div 
                                key={j} 
                                className="absolute w-1 h-1 bg-[#b0fffb] rounded-full animate-float"
                                style={{
                                  top: `${20 + Math.random() * 60}%`,
                                  left: `${20 + Math.random() * 60}%`,
                                  animationDelay: `${Math.random() * 2}s`,
                                  animationDuration: `${2 + Math.random() * 2}s`
                                }}
                              />
                            ))}
                          </div>
                      </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Enhanced Footer Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0c1c5c] via-[#0c1c5c]/80 to-transparent pointer-events-none"></div>
    </div>
  );
};

export default Main;